import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { getOptimizedImageUrl } from '../lib/utils';

const FirstGlobalChallengePage = () => {
    return (
        <div className="min-h-screen bg-cream" data-testid="fgc-page">
            <Header />

            <main className="pt-20">
                {/* Hero Section */}
                <section className="relative py-20 md:py-28 overflow-hidden bg-[#FAF6F0]">
                    {/* Background Image with Overlay */}
                    <div className="absolute inset-0">
                        <img
                            src={getOptimizedImageUrl('/assets/images/india_bg.png', 1920)}
                            srcSet={`
                                ${getOptimizedImageUrl('/assets/images/india_bg.png', 800)} 800w,
                                ${getOptimizedImageUrl('/assets/images/india_bg.png', 1200)} 1200w,
                                ${getOptimizedImageUrl('/assets/images/india_bg.png', 1920)} 1920w
                            `}
                            sizes="100vw"
                            alt="FIRST Global India Banner"
                            className="w-full h-full object-cover object-center opacity-85"
                            loading="eager"
                        />
                        {/* Soft gradient overlay to blend top and bottom */}
                        <div className="absolute inset-0 bg-gradient-to-b from-[#FAF6F0]/40 via-transparent to-cream-dark" />
                        <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-cream-dark via-cream-dark/80 to-transparent" />
                    </div>

                    <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="text-center max-w-3xl mx-auto">
                            <div className="bg-cream/75 border border-pragmatic/30 rounded-3xl p-8 md:p-10 shadow-xl shadow-dwarfpony/5 hover:shadow-2xl transition-all duration-300">
                                <span className="inline-block text-dwarfpony text-xs font-bold tracking-widest uppercase mb-4 px-4 py-1.5 bg-dwarfpony/10 rounded-full border border-dwarfpony/20">
                                    FIRST Global Challenge
                                </span>
                                <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-midnight mb-6 leading-tight tracking-tight">
                                    Representing India on the <span className="block sm:inline text-transparent bg-clip-text bg-gradient-to-r from-dwarfpony to-dwarfpony-dark">World Stage</span>
                                </h1>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Content Section */}
                <section className="py-20 bg-cream">
                    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-heavyrain text-lg leading-relaxed space-y-8">
                        <div>
                            <h2 className="text-2xl font-bold text-deepwalnut mb-4">FIRST Global Challenge</h2>
                            <p className="mb-4">
                                The FIRST Global Challenge is an international robotics competition that brings together students from over 190 countries on one global platform. It is designed to inspire young people to come together, think creatively, and work towards solving real-world challenges.
                            </p>
                            <p className="mb-4">
                                While building and competing with a robot is a key part of the experience, what truly defines the FIRST Global Challenge is the environment it creates. Students collaborate across cultures, exchange ideas, and learn from one another, making it as much about people and perspectives as it is about technology.
                            </p>
                            <p>
                                It is a unique opportunity to step beyond the classroom and be part of something global, meaningful, and memorable.
                            </p>
                        </div>

                        <div>
                            <h2 className="text-2xl font-bold text-deepwalnut mb-4">Why You Should Participate</h2>
                            <p className="mb-4">
                                Participating in the FIRST Global Challenge allows you to represent your country while being part of an international community of students who share a similar drive to learn and create.
                            </p>
                            <p className="mb-4">
                                It encourages you to take initiative, work as a team, and approach challenges with a problem-solving mindset. More importantly, it gives you exposure to new cultures, new ways of thinking, and new possibilities.
                            </p>
                            <p>
                                It is not just about participating in a competition — it is about experiencing something that broadens your perspective and shapes your journey ahead.
                            </p>
                        </div>

                        <div>
                            <h2 className="text-2xl font-bold text-deepwalnut mb-4">What You Gain</h2>
                            <p className="mb-4">
                                By the end of the experience, you gain much more than technical knowledge.
                            </p>
                            <p className="mb-4">
                                You develop skills such as teamwork, critical thinking, and adaptability. You build confidence by working on something real and presenting it on a global stage. You form meaningful connections and friendships with students from around the world.
                            </p>
                            <p className="mb-4">
                                In addition, teams have the opportunity to earn international recognition through various awards, celebrating both technical excellence and collaboration.
                            </p>
                            <p>
                                Most importantly, you leave with a stronger sense of what you are capable of, and a clearer view of the opportunities that lie ahead.
                            </p>
                        </div>

                        {/* Video Section */}
                        <div className="mt-16 pt-8 border-t border-pragmatic/20">
                            <h2 className="text-2xl font-bold text-deepwalnut mb-8 text-center">What is FIRST Global?</h2>
                            <div className="relative w-full overflow-hidden rounded-2xl shadow-xl" style={{ paddingTop: '56.25%' }}>
                                <iframe
                                    title="FIRST Global Challenge Video"
                                    className="absolute top-0 left-0 w-full h-full"
                                    src="https://www.youtube.com/embed/6YnnePHGRww?rel=0"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                    allowFullScreen
                                ></iframe>
                            </div>
                        </div>
                    </div>
                </section>

                {/* CTA */}
                <section className="py-20 bg-gradient-to-br from-midnight to-richolive-dark">
                    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                        <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                            Ready to Shape the Future?
                        </h2>
                        <p className="text-heavyrain text-lg mb-8">
                            Join us in our mission to make STEM education accessible to every student in India.
                        </p>
                        <Link to="/contact">
                            <Button className="bg-gradient-to-r from-dwarfpony to-dwarfpony-dark hover:from-dwarfpony-dark hover:to-dwarfpony text-white font-semibold px-8 py-6 text-lg shadow-xl shadow-dwarfpony/25 group">
                                Apply Now
                                <ArrowRight size={20} className="ml-2 group-hover:translate-x-1 transition-transform" />
                            </Button>
                        </Link>
                    </div>
                </section>
            </main>

            <Footer />
        </div>
    );
};

export default FirstGlobalChallengePage;
